import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  BookOpenCheck,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { logModuloEvento } from '../../utils/bitacora';
import {
  btnDanger,
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
  selectCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';

type EstadoLote = 'borrador' | 'preparado' | 'enviado_erp' | 'anulado';

interface CosteoLote {
  id: string;
  empresa_id: number;
  periodo_desde: string;
  periodo_hasta: string;
  estado: EstadoLote;
  origen: string | null;
  total_mano_obra: number | null;
  total_maquinaria: number | null;
  total_costo: number | null;
  preparado_at: string | null;
  enviado_erp_at: string | null;
  observacion: string | null;
  created_at: string;
}

interface CosteoDetalle {
  id: string;
  costeo_lote_id: string;
  boleta_numero: string | null;
  fecha: string;
  semana: string | null;
  finca_nombre: string | null;
  lote_num: string | null;
  bloque_num: string | null;
  estadio_codigo: string | null;
  labor_nombre: string | null;
  modalidad: string | null;
  supervisor_nombre: string | null;
  equipo_codigo: string | null;
  equipo_nombre: string | null;
  horas_ordinarias: number | null;
  horas_extra: number | null;
  cantidad_produccion: number | null;
  costo_mano_obra: number | null;
  costo_maquinaria: number | null;
  costo_total: number | null;
  cuenta_contable: string | null;
  cuenta_nombre: string | null;
  centro_costo: string | null;
  estado_contable: string | null;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtCRC(v?: number | null) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return 'CRC ' + n.toLocaleString('es-CR', { maximumFractionDigits: 0 });
}

function fmtNum(v?: number | null) {
  const n = Number(v ?? 0);
  return n ? n.toFixed(2) : '-';
}

function fmtDate(s?: string | null) {
  if (!s) return '-';
  return new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function EstadoBadge({ estado }: { estado: EstadoLote }) {
  const map: Record<EstadoLote, { label: string; color: string; bg: string; border: string }> = {
    borrador: { label: 'Borrador', color: 'var(--ink-muted)', bg: 'var(--surface-overlay)', border: 'var(--line)' },
    preparado: { label: 'Preparado', color: 'var(--tone-info-text)', bg: 'var(--tone-info-bg)', border: 'var(--tone-info-border)' },
    enviado_erp: { label: 'Enviado ERP', color: 'var(--tone-success-text)', bg: 'var(--tone-success-bg)', border: 'var(--tone-success-border)' },
    anulado: { label: 'Anulado', color: '#f87171', bg: 'rgba(127,29,29,0.25)', border: 'rgba(248,113,113,0.35)' },
  };
  const item = map[estado];
  return (
    <span className="inline-flex rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: item.color, background: item.bg, border: `1px solid ${item.border}` }}>
      {item.label}
    </span>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string | number; tone: string }) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>{label}</div>
      <div className="font-mono text-2xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

export function LotesCosteoLaboresPage() {
  const empresaId = useEmpresaId();
  const [fechaDesde, setFechaDesde] = useState(addDays(today(), -30));
  const [fechaHasta, setFechaHasta] = useState(today());
  const [estado, setEstado] = useState<'todos' | EstadoLote>('todos');
  const [q, setQ] = useState('');
  const [lotes, setLotes] = useState<CosteoLote[]>([]);
  const [detalle, setDetalle] = useState<CosteoDetalle[]>([]);
  const [seleccionadoId, setSeleccionadoId] = useState('');
  const [loading, setLoading] = useState(true);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const permisos = useAppPermisos();

  const seleccionado = useMemo(
    () => lotes.find((l) => l.id === seleccionadoId) || lotes[0] || null,
    [lotes, seleccionadoId]
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    setMensaje('');

    let query = supabase
      .from('campo_costeo_lotes')
      .select('id, empresa_id, periodo_desde, periodo_hasta, estado, origen, total_mano_obra, total_maquinaria, total_costo, preparado_at, enviado_erp_at, observacion, created_at')
      .eq('empresa_id', empresaId)
      .gte('periodo_hasta', fechaDesde)
      .lte('periodo_desde', fechaHasta)
      .order('created_at', { ascending: false });

    if (estado !== 'todos') query = query.eq('estado', estado);

    const { data, error: loadError } = await query;

    if (loadError) {
      setError(
        loadError.code === '42P01'
          ? 'Falta aplicar la migracion de costeo contable para ver lotes preparados.'
          : loadError.message
      );
      setLotes([]);
      setDetalle([]);
      setSeleccionadoId('');
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as CosteoLote[];
    setLotes(rows);
    setSeleccionadoId((prev) => (rows.some((l) => l.id === prev) ? prev : rows[0]?.id || ''));
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta, estado]);

  const cargarDetalle = useCallback(async (loteId: string) => {
    if (!loteId) {
      setDetalle([]);
      return;
    }
    setDetalleLoading(true);
    setError('');

    const { data, error: detalleError } = await supabase
      .from('campo_costeo_lote_detalle')
      .select('id, costeo_lote_id, boleta_numero, fecha, semana, finca_nombre, lote_num, bloque_num, estadio_codigo, labor_nombre, modalidad, supervisor_nombre, equipo_codigo, equipo_nombre, horas_ordinarias, horas_extra, cantidad_produccion, costo_mano_obra, costo_maquinaria, costo_total, cuenta_contable, cuenta_nombre, centro_costo, estado_contable')
      .eq('costeo_lote_id', loteId)
      .order('fecha', { ascending: true })
      .order('boleta_numero', { ascending: true });

    if (detalleError) {
      setError(detalleError.message);
      setDetalle([]);
    } else {
      setDetalle((data ?? []) as CosteoDetalle[]);
    }
    setDetalleLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cargarDetalle(seleccionado?.id || ''); }, [seleccionado?.id, cargarDetalle]);

  const filtrados = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return lotes;
    return lotes.filter((l) => [
      l.id,
      l.estado,
      l.origen,
      l.observacion,
      l.periodo_desde,
      l.periodo_hasta,
    ].join(' ').toLowerCase().includes(term));
  }, [lotes, q]);

  const totales = useMemo(() => filtrados.reduce((acc, l) => ({
    lotes: acc.lotes + 1,
    costo: acc.costo + Number(l.total_costo ?? 0),
    manoObra: acc.manoObra + Number(l.total_mano_obra ?? 0),
    maquinaria: acc.maquinaria + Number(l.total_maquinaria ?? 0),
  }), { lotes: 0, costo: 0, manoObra: 0, maquinaria: 0 }), [filtrados]);

  async function cambiarEstadoLote(nuevoEstado: 'enviado_erp' | 'anulado') {
    if (!seleccionado) return;
    setSaving(true);
    setError('');
    setMensaje('');

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id ?? null;
    const payload: Record<string, unknown> = {
      estado: nuevoEstado,
      updated_by: userId,
    };

    if (nuevoEstado === 'enviado_erp') {
      payload.enviado_erp_por = userId;
      payload.enviado_erp_at = new Date().toISOString();
    }

    const { error: updateError } = await supabase
      .from('campo_costeo_lotes')
      .update(payload)
      .eq('id', seleccionado.id);

    if (updateError) {
      setError(updateError.message);
    } else {
      logModuloEvento({
        empresaId,
        modulo: 'campo_labores',
        accion: nuevoEstado === 'enviado_erp' ? 'lote_enviado_erp' : 'lote_anulado',
        entidad: 'campo_costeo_lotes',
        entidadId: seleccionado.id,
        descripcion: `Lote de costeo ${seleccionado.id.slice(0, 8)} -> ${nuevoEstado}`,
      });
      setMensaje(nuevoEstado === 'enviado_erp' ? 'Lote marcado como enviado a ERP-MYA.' : 'Lote anulado.');
      await cargar();
    }
    setSaving(false);
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className={pageTitleCls}>Lotes de Costeo</h1>
            <p className={pageSubtitleCls}>Historial de lotes preparados desde Labores para revision y envio a ERP-MYA</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5 xl:flex xl:items-end">
            <div>
              <label className={labelCls}>Desde</label>
              <input type="date" className={inputCls} value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Hasta</label>
              <input type="date" className={inputCls} value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Estado</label>
              <select className={selectCls} value={estado} onChange={(e) => setEstado(e.target.value as typeof estado)}>
                <option value="todos">Todos</option>
                <option value="preparado">Preparados</option>
                <option value="enviado_erp">Enviados ERP</option>
                <option value="anulado">Anulados</option>
                <option value="borrador">Borrador</option>
              </select>
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        {mensaje && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-success-bg)', borderColor: 'var(--tone-success-border)', color: 'var(--tone-success-ink)' }}>
            <BookOpenCheck size={17} style={{ color: 'var(--tone-success-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{mensaje}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
          <Kpi label="Lotes" value={totales.lotes} tone="var(--tone-info-text)" />
          <Kpi label="Costo total" value={fmtCRC(totales.costo)} tone="var(--tone-success-text)" />
          <Kpi label="Mano de obra" value={fmtCRC(totales.manoObra)} tone="var(--tone-purple-text)" />
          <Kpi label="Maquinaria" value={fmtCRC(totales.maquinaria)} tone="var(--tone-warn-text)" />
        </div>

        <div className="mb-4 rounded-lg border p-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <label className={labelCls}>Buscar lote</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
            <input className={`${inputCls} pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="ID, estado, periodo u observacion" />
          </div>
        </div>

        <div className="rounded-lg border" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <div className="grid grid-cols-[44px_minmax(150px,1fr)_minmax(190px,1.2fr)_minmax(130px,0.7fr)_minmax(110px,0.6fr)] border-b px-3 py-2 text-[11px] font-bold uppercase tracking-wider" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)', background: 'var(--surface-overlay)' }}>
            <div />
            <div>Lote</div>
            <div>Periodo</div>
            <div className="text-right">Total</div>
            <div>Estado</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
              <Loader2 size={18} className="animate-spin" />
              Cargando lotes...
            </div>
          ) : filtrados.length === 0 ? (
            <div className="py-16 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>No hay lotes de costeo en el rango.</div>
          ) : filtrados.map((l) => {
            const abierto = seleccionado?.id === l.id;
            return (
              <div
                key={l.id}
                className="border-b last:border-b-0"
                style={{
                  borderColor: abierto ? 'var(--emp-accent)' : 'var(--line-dim)',
                  boxShadow: abierto ? 'inset 4px 0 0 var(--emp-accent)' : undefined,
                }}
              >
                <button
                  type="button"
                  className="grid w-full grid-cols-[44px_minmax(150px,1fr)_minmax(190px,1.2fr)_minmax(130px,0.7fr)_minmax(110px,0.6fr)] items-center px-3 py-3 text-left transition-colors hover:bg-surface-overlay/60"
                  onClick={() => setSeleccionadoId(abierto ? '' : l.id)}
                  style={abierto ? { background: 'color-mix(in srgb, var(--emp-accent-bg) 72%, var(--surface-overlay))' } : undefined}
                >
                  <div style={{ color: 'var(--ink-faint)' }}>{abierto ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</div>
                  <div>
                    <div className="font-mono text-sm font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{l.id.slice(0, 8)}</div>
                    <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{fmtDate(l.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{fmtDate(l.periodo_desde)} - {fmtDate(l.periodo_hasta)}</div>
                    <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{l.origen || '-'}</div>
                  </div>
                  <div className="text-right font-mono text-sm font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(l.total_costo)}</div>
                  <div><EstadoBadge estado={l.estado} /></div>
                </button>

                {abierto && (
                  <div className="border-t px-3 pb-3" style={{ borderColor: 'var(--emp-accent)', background: 'var(--surface-deep)' }}>
                    <div className="flex flex-col gap-3 rounded-t-lg border-x border-t px-4 py-3 md:flex-row md:items-center md:justify-between" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                      <div>
                        <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Detalle {l.id.slice(0, 8)}</div>
                        <div className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                          {detalle.length} linea(s) congeladas - {fmtCRC(l.total_costo)}
                        </div>
                      </div>
                      {l.estado === 'preparado' && permisos.perm_administrar && (
                        <div className="flex flex-wrap gap-2">
                          <button type="button" className={`${btnSecondary} flex items-center gap-2`} disabled={saving} onClick={() => cambiarEstadoLote('enviado_erp')}>
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                            Marcar enviado
                          </button>
                          <button type="button" className={`${btnDanger} flex items-center gap-2`} disabled={saving} onClick={() => cambiarEstadoLote('anulado')}>
                            <Ban size={14} />
                            Anular
                          </button>
                        </div>
                      )}
                    </div>

                    <div className={`${tableWrapCls} rounded-b-lg rounded-t-none`} style={{ borderColor: 'var(--line)' }}>
                      <table className="min-w-[1120px] w-full border-collapse">
                        <thead className={theadCls}>
                          <tr>
                            <th className={thCls}>Boleta</th>
                            <th className={thCls}>Ubicacion</th>
                            <th className={thCls}>Labor / equipo</th>
                            <th className={thCls}>Cuenta</th>
                            <th className={`${thCls} text-right`}>Horas</th>
                            <th className={`${thCls} text-right`}>Unid.</th>
                            <th className={`${thCls} text-right`}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalleLoading ? (
                            <tr><td colSpan={7} className={`${tdCls} py-12 text-center`}><Loader2 size={18} className="mx-auto mb-2 animate-spin" />Cargando detalle...</td></tr>
                          ) : detalle.length === 0 ? (
                            <tr><td colSpan={7} className={`${tdCls} py-12 text-center`}>El lote no tiene detalle.</td></tr>
                          ) : detalle.map((d, index) => (
                            <tr
                              key={d.id}
                              className={trCls}
                              style={{ background: index % 2 === 0 ? 'var(--surface-overlay)' : 'var(--surface-raised)' }}
                            >
                              <td className={tdCls}>
                                <div className="font-mono font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{d.boleta_numero || '-'}</div>
                                <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{fmtDate(d.fecha)} - Sem. {d.semana || '-'}</div>
                              </td>
                              <td className={tdCls}>
                                <div style={{ color: 'var(--ink)' }}>{d.finca_nombre || '-'}</div>
                                <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Lote {d.lote_num || '-'} / Bloque {d.bloque_num || '-'} / Est. {d.estadio_codigo || '-'}</div>
                              </td>
                              <td className={tdCls}>
                                <div style={{ color: 'var(--ink)' }}>{d.labor_nombre || '-'}</div>
                                <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{d.modalidad || '-'} {d.equipo_nombre ? `/ ${d.equipo_nombre}` : ''}</div>
                              </td>
                              <td className={tdCls}>
                                <div className="font-mono font-bold" style={{ color: 'var(--tone-purple-text)' }}>{d.cuenta_contable || '-'}</div>
                                <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{d.cuenta_nombre || '-'}</div>
                              </td>
                              <td className={`${tdCls} text-right font-mono`}>{fmtNum(Number(d.horas_ordinarias ?? 0) + Number(d.horas_extra ?? 0))}</td>
                              <td className={`${tdCls} text-right font-mono`}>{fmtNum(d.cantidad_produccion)}</td>
                              <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(d.costo_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default LotesCosteoLaboresPage;
